// @flow

import React from 'react';
import styled from 'styled-components';

import { currency } from 'client/utils/format';
import { DividerNoMargin } from 'client/components/divider';
import { filterCoveredMembers } from 'client/utils/member';
import { ModifyPlanLinksContainer, LinkTranslate } from 'client/components/cart';
import COLORS from 'client/constants/colors';
import ContentfulTranslate from 'client/components/ContentfulTranslate';
import DateFormat from 'client/components/date/format';
import LinkButton from 'client/components/button/linkButton';
import MemberNames from 'client/components/member/names';
import CORE_PRODUCT_TYPES from 'client/constants/productTypes';
import useLoader from 'client/hooks/useLoader';

import { trackChangePlanInCart, trackRemovePlanFromCart } from 'routes/cart/util/tracking';
import { PriceContainer, SectionContent, SectionHeader } from 'routes/cart/components/shared';

import type { AppType } from 'web-core/types/ifp-app';
import type { MemberType } from 'web-core/types/member';

const FlexContainer = styled.div`
  align-items: baseline;
  flex: 1;
  display: flex;
  justify-content: space-between;
`;

const ExclamationContainer = styled.div`
  text-align: center;
  width: 1.5rem;

  .icon-exclamation-border {
    color: ${COLORS.ORANGE_2};
  }
`;

const PlanNameContainer = styled(SectionHeader)`
  color: ${({ excludePlan }) => excludePlan && COLORS.GRAY_4};
`;

const StyledPriceContainer = styled(PriceContainer)`
  ${({ excludePlan }) =>
    excludePlan &&
    `
  color ${COLORS.GRAY_4};
  text-decoration: line-through;
  `}
`;

const StyledLinkTranslate = styled(LinkTranslate)`
  color: $black;
  text-decoration-color: rgb(94,93,93);
  text-decoration-line: underline;
  font-weight: 400;
`;

const StyledModifyPlanLinksContainer = styled(ModifyPlanLinksContainer)`
  padding-bottom: 0rem;
`;

type CartPlanItemProps = {
  allowRemovePlan: boolean,
  app_id: string,
  carrierName: string,
  changeHref: string,
  effectiveDate: Date,
  excludePlan: boolean,
  fetchActiveApps: Function,
  fetchCart: Function,
  locale: string,
  members: Array<MemberType>,
  monthlyPremium: string,
  onError: Function,
  onRemovePlan: Function,
  planName: string,
  resetApp: Function,
  type: AppType,
};

const CartPlanItem = ({
  allowRemovePlan,
  app_id,
  carrierName,
  changeHref,
  effectiveDate,
  excludePlan,
  fetchActiveApps,
  fetchCart,
  locale,
  members,
  monthlyPremium,
  onError,
  onRemovePlan,
  planName,
  resetApp,
  type,
}: CartPlanItemProps) => {
  const { isLoading, setLoading, Loader } = useLoader(false);
  const handleRemovePlan = async () => {
    try {
      onRemovePlan();
      setLoading(true);
      await resetApp({ app_id });
      await Promise.all([fetchCart(), fetchActiveApps()]);
      trackRemovePlanFromCart(type);
    } catch (e) {
      onError();
    }
  };
  const handleChangePlan = () => {
    trackChangePlanInCart(type);
    window.location.href = changeHref;
    return null;
  };

  const PlanDetailAndOptions = () => {
    const PlanName = () => (
      <PlanNameContainer excludePlan={excludePlan}>
        {`${carrierName} ${planName}`}
      </PlanNameContainer>
    );

    const CoveredMembersAndDate = () => (
      <React.Fragment>
        <SectionContent>
          {/* <ContentfulTranslate path="cart.memberNamePrefix" />
          <MemberNames memberNames={filterCoveredMembers(members)} /> */}
        </SectionContent>
        <SectionContent>
          <ContentfulTranslate path="cart.coverageStartDatePrefix" />
          <DateFormat date={effectiveDate} formatString="MMMM D, YYYY" locale={locale} />
        </SectionContent>
      </React.Fragment>
    );
    const ModifyOptions = () => (
      <div>
        <StyledModifyPlanLinksContainer>
          {/* Since we only have one plan for vision, we won't display the change link */}
          {type !== CORE_PRODUCT_TYPES.VISION && (
            <div className="pad-right">
              <LinkButton className="transparent" onClick={handleChangePlan}>
                <StyledLinkTranslate path="app.cart.card.change" />
              </LinkButton>
            </div>
          )}
          {allowRemovePlan && (
            <div>
              <LinkButton className="transparent" onClick={handleRemovePlan}>
                <StyledLinkTranslate path="app.cart.card.remove" />
              </LinkButton>
            </div>
          )}
        </StyledModifyPlanLinksContainer>
      </div>
    );
    return (
      <div className="pad-right">
        <PlanName />
        <CoveredMembersAndDate />
        <ModifyOptions />
      </div>
    );
  };

  const PlanPrice = () => (
    <div>
      <StyledPriceContainer excludePlan={excludePlan}>
        {currency(monthlyPremium, 2)}
      </StyledPriceContainer>
    </div>
  );

  if (isLoading) {
    return (
      <div className="text-center">
        <Loader />
      </div>
    );
  }

  return (
    <React.Fragment>
      <FlexContainer>
        {excludePlan && (
          <ExclamationContainer>
            <span className="icon-exclamation-border" />
          </ExclamationContainer>
        )}
        <FlexContainer>
          <PlanDetailAndOptions />
          <PlanPrice />
        </FlexContainer>
      </FlexContainer>
      <DividerNoMargin />
    </React.Fragment>
  );
};

export default CartPlanItem;
